import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Mail, Phone, MapPin, 
  Briefcase, Calendar, GraduationCap, 
  Award, Clock, User, Camera, Lock,
  Check, Loader2, AlertCircle, Edit2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AppShell from '../components/layout/AppShell';
import { updateMe } from '../api/auth-api';
import { uploadAvatar } from '../lib/uploads';
import { useQueryClient } from '@tanstack/react-query';
import CoFounderBadge from '../components/ui/CoFounderBadge';
import { isCoFounder } from '../utils/cofounderBadge';

const capitalizeFirst = (str) => {
  if (!str || typeof str !== 'string') return '';
  const trimmed = str.trim();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

/** Maps raw yearOfStudy DB values to human-readable labels. */
const YEAR_LABELS = {
  '1': '1st Year',
  '2': '2nd Year',
  '3': '3rd Year',
  '4': '4th Year',
  '5': '5th Year',
  'intern': 'Intern',
};

/** Resizes to fit within maxWidth × maxHeight and returns a JPEG Blob. */
const compressImageToBlob = (file, maxWidth = 300, maxHeight = 300) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        // A Blob, not a data URL: the photo is uploaded to storage now, and a
        // base64 string would inflate it by a third and could not be PUT with a
        // matching Content-Type.
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('Failed to encode image'))),
          'image/jpeg',
          0.85
        );
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

const PRESET_ROLES = [
  'Practice Owner / Director',
  'Senior Veterinarian',
  'Head Veterinarian',
  'Associate Veterinarian',
  'Consultant / Specialist',
  'Emergency & Critical Care Vet',
  'Resident / Intern',
  'Academician / Researcher',
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, updateUser } = useAuth();
  const fileInputRef = useRef(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isCustomRoleInput, setIsCustomRoleInput] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    avatarUrl: '',
    yearOfStudy: '',
    clinicName: '',
    jobRoleSelect: '',
    customJobRole: '',
    location: '',
    primaryPractice: '',
    specializations: '',
    yearsOfExperience: '',
  });

  const [avatarPreview, setAvatarPreview] = useState('');
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Sync form data whenever user loads or when entering edit mode
  useEffect(() => {
    if (user) {
      const initialJobRole = user.jobRole || '';
      const isPreset = PRESET_ROLES.includes(initialJobRole);
      setIsCustomRoleInput(initialJobRole ? !isPreset : false);

      setFormData({
        name: user.name || '',
        // The KEY, not the signed URL the API returns alongside it. Submitting a
        // signed URL would store a link that expires in hours; avatarKey is the
        // raw stored value and is what the server expects back unchanged.
        avatarUrl: user.avatarKey ?? user.avatarUrl ?? '',
        yearOfStudy: user.studentProfile?.yearOfStudy || '',
        clinicName: user.clinicName || '',
        jobRoleSelect: isPreset ? initialJobRole : (initialJobRole ? 'Other' : ''),
        customJobRole: initialJobRole,
        location: user.location || '',
        primaryPractice: user.primaryPractice || '',
        specializations: user.role === 'student' ? '' : (user.specializations?.join(', ') || user.specialization || ''),
        yearsOfExperience: user.yearsOfExperience || '',
      });
      setAvatarPreview(user.avatarUrl || '');
    }
  }, [user, isEditing]);

  if (!user) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-indigo"></div>
        </div>
      </AppShell>
    );
  }

  const isStudent = user.role === 'student';
  const joinedDate = new Date(user.createdAt).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });

  const handleStartEdit = () => {
    setError('');
    setSuccessMsg('');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setError('');
    setIsEditing(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Auto-capitalize first letter live while typing for text fields
    if (['name', 'clinicName', 'customJobRole', 'location', 'primaryPractice'].includes(name)) {
      const capitalized = value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
      setFormData((prev) => ({ ...prev, [name]: capitalized }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 1 * 1024 * 1024) {
      setError('Profile photo file size must not exceed 1MB.');
      return;
    }

    setIsUploadingPhoto(true);
    setError('');

    try {
      // Resize first — a 300×300 JPEG is a few tens of KB, so the upload is
      // quick and the stored object stays small.
      const blob = await compressImageToBlob(file);
      // Preview from the resized blob, not the original: what is shown is then
      // exactly what was stored.
      setAvatarPreview(URL.createObjectURL(blob));
      // The photo goes straight to B2 now; the key is submitted with the form
      // and the server verifies and files it on save.
      const key = await uploadAvatar(blob);
      setFormData((prev) => ({
        ...prev,
        avatarUrl: key
      }));
    } catch (err) {
      console.error('[ProfilePhotoSelect] Failed to upload image:', err);
      setError(err?.response?.data?.message || 'Failed to upload photo. Please try another image.');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    setSuccessMsg('');

    try {
      const rawJobRole = isCustomRoleInput
        ? formData.customJobRole
        : (formData.jobRoleSelect === 'Other' ? formData.customJobRole : formData.jobRoleSelect);

      const capitalizedJobRole = capitalizeFirst(rawJobRole);
      const capitalizedClinic = capitalizeFirst(formData.clinicName);
      const capitalizedPractice = capitalizeFirst(formData.primaryPractice);
      const capitalizedLocation = capitalizeFirst(formData.location);
      const capitalizedName = capitalizeFirst(formData.name);

      const payload = {
        ...user,
        name: capitalizedName || user.name,
        avatarUrl: formData.avatarUrl,
      };

      if (isStudent) {
        payload.studentProfile = {
          ...user?.studentProfile,
          yearOfStudy: formData.yearOfStudy
        };
      } else {
        payload.clinicName = capitalizedClinic;
        payload.jobRole = capitalizedJobRole;
        payload.location = capitalizedLocation;
        payload.primaryPractice = capitalizedPractice;
        payload.specialization = formData.specializations ? formData.specializations.split(',').map(s => s.trim())[0] : null;
        payload.specializations = formData.specializations ? formData.specializations.split(',').map(s => s.trim()).filter(Boolean) : [];
        payload.yearsOfExperience = formData.yearsOfExperience !== '' ? Number(formData.yearsOfExperience) : null;
      }

      // 1. Call Backend Update API
      //
      // Keep the response. It carries the saved row with `avatarUrl` signed for
      // display and the raw `avatarKey` beside it. The payload built above is
      // not a substitute: its avatarUrl is the storage key, which as an <img
      // src> resolves against our own origin, 404s, and renders as alt text.
      let saved = null;
      try {
        const result = await updateMe(payload);
        saved = result?.data ?? null;
      } catch (backendErr) {
        console.warn('[ProfileUpdate] Backend update notice:', backendErr?.message);
      }

      // 2. Update Auth Context & TanStack Query cache
      //
      // Server values win, but merge over the payload so any field it does not
      // return is preserved. When the save failed there is nothing to trust, so
      // keep the avatar that is already on screen rather than writing a key.
      updateUser(
        saved
          ? { ...payload, ...saved }
          : { ...payload, avatarUrl: user.avatarUrl, avatarKey: user.avatarKey }
      );
      try {
        queryClient.invalidateQueries({ queryKey: ['currentUser'] });
        queryClient.invalidateQueries({ queryKey: ['cases'] });
      } catch (e) {
        console.warn('[ProfileUpdate] Invalidate queries notice:', e);
      }

      setSuccessMsg('Profile updated successfully!');
      setIsEditing(false);
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      console.error('[ProfileUpdate] Error:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to update profile.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="bg-gray-50/50 min-h-screen py-8 px-4 pb-20">
        <div className="max-w-4xl mx-auto space-y-6">
          
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft size={20} />
              </button>
              <h1 className="text-xl font-bold text-gray-900">My Profile</h1>
            </div>

            {!isEditing && (
              <button 
                onClick={handleStartEdit}
                className="px-5 py-2 bg-[#3b82f6] hover:bg-blue-600 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm flex items-center gap-2"
              >
                <Edit2 size={15} /> Edit Profile
              </button> 
            )}
          </div>

          {/* Feedback Banners */}
          {successMsg && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-medium px-4 py-3 rounded-xl flex items-center gap-2 shadow-sm animate-in fade-in">
              <Check size={16} className="text-emerald-600" />
              {successMsg}
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm font-medium px-4 py-3 rounded-xl flex items-center gap-2 shadow-sm animate-in fade-in">
              <AlertCircle size={16} className="text-red-600 shrink-0" />
              {error}
            </div>
          )}

          {/* Profile Card Container */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            
            {/* Gradient Banner */}
            <div className="h-40 bg-gradient-to-r from-[#3b82f6] to-[#8b5cf6]"></div>
            
            {/* ======================================================== */}
            {/* VIEW MODE (Read-only view)                               */}
            {/* ======================================================== */}
            {!isEditing ? (
              <div className="px-8 pb-10">
                {/* Avatar */}
                <div className="relative -mt-16 mb-6">
                  <div className="w-32 h-32 bg-white rounded-full p-2 flex items-center justify-center shadow-md">
                    <div className="w-full h-full bg-gray-50 rounded-full flex items-center justify-center border border-gray-100 text-[#8b5cf6] overflow-hidden">
                      {user.avatarUrl ? (
                        <img src={user.avatarUrl} alt={user.name} className="w-full h-full object-cover" />
                      ) : (
                        <User size={48} strokeWidth={1.5} />
                      )}
                    </div>
                  </div>
                </div>

                {/* Name & Role */}
                <div className="mb-10">
                  <h2 className="text-2xl font-bold text-gray-900">{user.name}</h2>
                  <p className="text-gray-500 text-sm font-medium mt-1 capitalize flex items-center gap-2">
                    {!isCoFounder(user) && <span>{user.role}</span>}
                    {user.jobRole && (
                      <span className="text-gray-600 font-medium capitalize">
                        {!isCoFounder(user) && '• '}{capitalizeFirst(user.jobRole)}
                      </span>
                    )}
                  </p>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-12">
                  
                  {/* Email */}
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 text-gray-400">
                      <Mail size={18} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium mb-1">Email</p>
                      <p className="text-sm font-medium text-gray-700">{user.email}</p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 text-gray-400">
                      <Phone size={18} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium mb-1">Phone</p>
                      <p className="text-sm font-medium text-gray-700">{user.phone || 'Not provided'}</p>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 text-gray-400">
                      <MapPin size={18} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium mb-1">Location</p>
                      <p className="text-sm font-medium text-gray-700 capitalize">
                        {user.location || (isStudent 
                          ? (user.studentProfile?.college?.city ? `${user.studentProfile.college.city}, ` : '') + (user.studentProfile?.college?.country || 'India')
                          : 'Not provided'
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Primary Practice */}
                  {!isStudent && (
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 text-gray-400">
                        <Briefcase size={18} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium mb-1">Primary Practice</p>
                        <p className="text-sm font-medium text-gray-700 capitalize">
                          {user.primaryPractice ? capitalizeFirst(user.primaryPractice) : 'Not provided'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Associated Clinic / Hospital */}
                  {!isStudent && (
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 text-gray-400">
                        <Briefcase size={18} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium mb-1">Associated Clinic/Hospital</p>
                        <p className="text-sm font-medium text-gray-700 capitalize">
                          {user.clinicName ? capitalizeFirst(user.clinicName) : 'Not provided'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Job Role */}
                  {!isStudent && (
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 text-gray-400">
                        <User size={18} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium mb-1">Job Role</p>
                        <p className="text-sm font-medium text-gray-700 capitalize">
                          {user.jobRole ? capitalizeFirst(user.jobRole) : 'Not provided'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* College Name (for Students only) */}
                  {isStudent && (
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 text-gray-400">
                        <GraduationCap size={18} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium mb-1">College</p>
                        <p className="text-sm font-medium text-gray-700">
                          {user.studentProfile?.college?.name || 'Not provided'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Experience / Year of Study */}
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 text-gray-400">
                      {isStudent ? <Award size={18} /> : <Clock size={18} />}
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium mb-1">
                        {isStudent ? 'Year of Study' : 'Experience'}
                      </p>
                      <p className="text-sm font-medium text-gray-700">
                        {isStudent
                          ? (YEAR_LABELS[user.studentProfile?.yearOfStudy] || user.studentProfile?.yearOfStudy || 'Not provided')
                          : (user.yearsOfExperience ? `${user.yearsOfExperience} years` : 'Not provided')
                        }
                      </p>
                    </div>
                  </div>

                  {/* Joined Date */}
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 text-gray-400">
                      <Calendar size={18} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium mb-1">Joined</p>
                      <p className="text-sm font-medium text-gray-700">{joinedDate}</p>
                    </div>
                  </div>

                </div>
              </div>
            ) : (
              /* ======================================================== */
              /* INLINE EDIT MODE (In-place Form)                         */
              /* ======================================================== */
              <form onSubmit={handleSave} className="px-8 pb-10">
                {/* Avatar with Upload button */}
                <div className="relative -mt-16 mb-8 flex flex-col items-start">
                  <div className="relative w-32 h-32 bg-white rounded-full p-2 shadow-md group">
                    <div className="w-full h-full bg-gray-50 rounded-full flex items-center justify-center border border-gray-100 text-[#8b5cf6] overflow-hidden">
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <User size={48} strokeWidth={1.5} />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute bottom-2 right-2 p-2 bg-[#3b82f6] text-white rounded-full hover:bg-blue-600 transition-colors shadow-md flex items-center justify-center"
                      title="Upload profile photo"
                    >
                      <Camera size={16} />
                    </button>
                  </div>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoSelect}
                  />

                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-2 text-xs font-semibold text-[#3b82f6] hover:text-blue-700 flex items-center gap-1 transition-colors"
                  >
                    {isUploadingPhoto ? 'Processing photo...' : 'Change Profile Photo'}
                  </button>
                </div>

                {/* Form Fields Grid */}
                <div className="space-y-6">
                  
                  {/* Row 1: Name & Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2.5 bg-[#F5F6F8] rounded-xl border border-transparent focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm text-gray-800 transition-all font-medium capitalize"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1">
                        Phone <Lock size={12} className="text-gray-400" />
                      </label>
                      <input
                        type="text"
                        value={user.phone || ''}
                        disabled
                        className="w-full px-4 py-2.5 bg-gray-100 text-gray-500 rounded-xl border border-gray-200 text-sm cursor-not-allowed"
                      />
                    </div>
                  </div>

                  {/* Row 2: Email & Joined Date */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1">
                        Email <Lock size={12} className="text-gray-400" />
                      </label>
                      <input
                        type="text"
                        value={user.email || ''}
                        disabled
                        className="w-full px-4 py-2.5 bg-gray-100 text-gray-500 rounded-xl border border-gray-200 text-sm cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1">
                        Joined Date <Lock size={12} className="text-gray-400" />
                      </label>
                      <input
                        type="text"
                        value={joinedDate}
                        disabled
                        className="w-full px-4 py-2.5 bg-gray-100 text-gray-500 rounded-xl border border-gray-200 text-sm cursor-not-allowed"
                      />
                    </div>
                  </div>

                  {/* Row 3: Associated Clinic / Hospital */}
                  {!isStudent && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Associated Clinic / Hospital</label>
                      <input
                        type="text"
                        name="clinicName"
                        value={formData.clinicName}
                        onChange={handleChange}
                        placeholder="e.g. Max Pet Care Clinic"
                        className="w-full px-4 py-2.5 bg-[#F5F6F8] rounded-xl border border-transparent focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm text-gray-800 transition-all font-medium capitalize"
                      />
                    </div>
                  )}

                  {/* Row 4: Job Role & Location */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {!isStudent && (
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="text-xs font-semibold text-gray-700">Job Role</label>
                          <button
                            type="button"
                            onClick={() => setIsCustomRoleInput(!isCustomRoleInput)}
                            className="text-[11px] text-[#3b82f6] hover:text-blue-700 font-semibold"
                          >
                            {isCustomRoleInput ? 'Select from list' : 'Type custom role'}
                          </button>
                        </div>

                        {isCustomRoleInput ? (
                          <input
                            type="text"
                            name="customJobRole"
                            value={formData.customJobRole}
                            onChange={handleChange}
                            placeholder="e.g. Dermatologist, Surgeon..."
                            className="w-full px-4 py-2.5 bg-[#F5F6F8] rounded-xl border border-transparent focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm text-gray-800 transition-all font-medium capitalize"
                          />
                        ) : (
                          <select
                            name="jobRoleSelect"
                            value={formData.jobRoleSelect}
                            onChange={(e) => {
                              handleChange(e);
                              if (e.target.value === 'Other') {
                                setIsCustomRoleInput(true);
                              }
                            }}
                            className="w-full px-4 py-2.5 bg-[#F5F6F8] rounded-xl border border-transparent focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm text-gray-800 transition-all font-medium capitalize"
                          >
                            <option value="">Select your role</option>
                            {PRESET_ROLES.map((role) => (
                              <option key={role} value={role}>{role}</option>
                            ))}
                            <option value="Other">Other (Type custom role)</option>
                          </select>
                        )}
                      </div>
                    )}

                    {/* Location */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Location (City, Country)</label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="e.g. Mumbai, India"
                        className="w-full px-4 py-2.5 bg-[#F5F6F8] rounded-xl border border-transparent focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm text-gray-800 transition-all font-medium capitalize"
                      />
                    </div>
                  </div>

                  {/* Row 5: Primary Practice & Years of Experience (or Year of Study for students) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {!isStudent && (
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Primary Practice</label>
                        <input
                          type="text"
                          name="primaryPractice"
                          value={formData.primaryPractice}
                          onChange={handleChange}
                          placeholder="e.g. Small Animal, Mixed Animal, Equine..."
                          className="w-full px-4 py-2.5 bg-[#F5F6F8] rounded-xl border border-transparent focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm text-gray-800 transition-all font-medium capitalize"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        {isStudent ? 'Year of Study' : 'Years of Experience'}
                      </label>
                      <input
                        type={isStudent ? 'text' : 'number'}
                        name={isStudent ? 'yearOfStudy' : 'yearsOfExperience'}
                        value={isStudent ? formData.yearOfStudy : formData.yearsOfExperience}
                        onChange={handleChange}
                        placeholder={isStudent ? 'e.g. Final Year (4th Year)' : 'e.g. 5'}
                        min={isStudent ? undefined : 0}
                        className="w-full px-4 py-2.5 bg-[#F5F6F8] rounded-xl border border-transparent focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm text-gray-800 transition-all font-medium"
                      />
                    </div>
                  </div>

                </div>

                {/* Form Action Buttons */}
                <div className="flex items-center justify-end gap-3 pt-8 mt-8 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="px-5 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-6 py-2.5 bg-[#3b82f6] hover:bg-blue-600 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm disabled:opacity-60 flex items-center gap-2"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Saving Changes...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>

              </form>
            )}

          </div>
        </div>
      </div>
    </AppShell>
  );
};

export default ProfilePage;
